import { GoalVisibilityFilter } from '@/common/constants/filter';
import { CompleteStatusCodes, StatusCode } from '@/common/constants/status';
import { authService } from '@/features/auth/services/authService';
import {
    NotificationType,
    UpdateContext,
} from '@/features/notification/constants/notification';
import {
    useGoalDeadlineChangedNotification,
    useGoalLeaderChangedNotification,
    useGoalPriorityChangedNotification,
    useGoalStateChangedNotification,
} from '@/features/notification/services/factoryNotificationServices';
import { useDeleteSchedule } from '@/features/notification/services/useNotificationQueries';
import {
    checkSendNotificationByStatusCode,
    isDifferent,
} from '@/features/notification/utils/notificationUtils';
import { workspaceService } from '@/features/workspace/services/workspaceService';
import { supabase, withSafeUsersArray } from '@tickitacka/shared';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { useAuthStore } from '@/stores/authStore';
import { useTeamStore } from '@/stores/teamStore';
import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
    Goal,
    GoalWithInfos,
    GoalWithProject,
    Todo,
} from '../../../common/types/databaseTypes';
import { useComments } from '../../comment/services/useCommentQueries';
import { CreateGoalParams, goalService } from '../services/goalService';
import { useInvalidateActivityLogs } from './activityQueries';
import { useTranslation } from 'react-i18next';

// 팀 내 모든 목표 목록 조회 훅(무한 스크롤 지원)
export function useGoalsByTeamId(
    teamId: string,
    userId?: string,
    filters?: {
        status?: string[];
        priority?: string[];
        leader?: string[];
        contributor?: string[];
        project?: string[];
    },
    groupBy?: string,
    orderBy = 'register_date',
    pageSize = 20,
    includeAllInGroup = true
) {
    const { t } = useTranslation();
    // React Hook 규칙에 따라 조건부로 Hook을 호출하지 않도록 수정
    return useInfiniteQuery({
        queryKey: [
            'goal',
            'team',
            teamId,
            { filters, groupBy, orderBy, pageSize },
        ],
        queryFn: async ({ pageParam = 0 }) => {
            const result = await goalService.getGoalsByTeamId(
                teamId,
                userId,
                filters,
                groupBy,
                orderBy,
                pageParam,
                pageSize,
                includeAllInGroup
            );
            return result;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            // 1. 빈 페이지면 더 이상 데이터 없음
            if (lastPage.length === 0) {
                console.log('더 이상 데이터가 없습니다 (빈 페이지)');
                return undefined;
            }

            // 2. 페이지 크기보다 작은 데이터가 온 경우 - 마지막 페이지
            if (lastPage.length < pageSize) {
                console.log('더 이상 데이터가 없습니다 (마지막 페이지)');
                return undefined;
            }

            // 3. 다음 페이지 번호 반환
            const nextPage = allPages.length;
            console.log(`다음 페이지: ${nextPage}`);
            return nextPage;
        },
        enabled: !!teamId, // teamId가 있을 때만 쿼리 실행
        select: data => ({
            ...data,
            pages: data.pages.map(goalsInPage =>
                withSafeUsersArray(goalsInPage, t, {
                    userFields: ['leader', 'participants'],
                })
            ),
        }),
    });
}

// 여러 팀의 목표를 통합 조회하는 훅 (공유된 목표 포함)
export function useMultiTeamGoals(
    teamIds: string[],
    userId?: string,
    enabled?: boolean
) {
    // 유효한 팀 ID만 필터링
    const validTeamIds = useMemo(
        () => (teamIds || []).filter(id => !!id),
        [teamIds]
    );

    const isEnabled =
        enabled !== undefined
            ? validTeamIds.length > 0 && enabled
            : validTeamIds.length > 0;

    // 각 팀에 대한 목표 쿼리 생성
    const teamGoalQueries = useMemo(
        () =>
            validTeamIds.map(teamId => ({
                queryKey: ['goal', 'team', teamId],
                queryFn: () => goalService.getGoalsByTeamId(teamId, userId),
                enabled: isEnabled,
            })),
        [validTeamIds, userId, isEnabled]
    );

    // 각 팀별 쿼리 결과
    const teamQueryResults = useQueries({
        queries: teamGoalQueries,
    });

    // 모든 팀의 데이터 통합 및 중복 제거
    const { data, isLoading, isError, error } = useMemo(() => {
        // 로딩 및 에러 상태 확인
        const isLoading = teamQueryResults.some(result => result.isLoading);
        const isError = teamQueryResults.some(result => result.isError);
        const error = teamQueryResults.find(result => result.error)?.error;

        // 모든 팀의 목표 데이터 합치기
        const allGoals = teamQueryResults.flatMap(result => result.data || []);

        // 중복 제거 - 목표 ID 기준
        const finalGoalMap = new Map<string, GoalWithInfos>();
        allGoals.forEach(goal => {
            finalGoalMap.set(goal.id, goal);
        });

        return {
            data: Array.from(finalGoalMap.values()),
            isLoading,
            isError,
            error,
        };
    }, [teamQueryResults]);

    return {
        data,
        isLoading,
        isError,
        error,
        teamQueryResults, // 개별 팀 쿼리 결과도 필요한 경우 제공
    };
}

// 목표 상세 정보 조회 훅
export const useGoalDetailsById = (goalId: string | undefined) => {
    const queryClient = useQueryClient();
    // Supabase 실시간 구독과 React Query 캐시 활용 결합
    useEffect(() => {
        if (!goalId) return;

        // 디바운스 처리 로직
        let debounceTimer: NodeJS.Timeout | null = null;

        const debouncedInvalidate = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                void queryClient.invalidateQueries({
                    queryKey: ['goal', goalId, 'detail'],
                });
            }, 300);
        };

        // Supabase 채널 설정
        const channel = supabase.channel(`goal-${goalId}-realtime`);

        // INSERT와 DELETE 이벤트만 감지
        channel.on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'todo',
                filter: `goal_id=eq.${goalId}`,
            },
            debouncedInvalidate
        );

        channel.on(
            'postgres_changes',
            {
                event: 'DELETE',
                schema: 'public',
                table: 'todo',
                filter: `goal_id=eq.${goalId}`,
            },
            debouncedInvalidate
        );

        // UPDATE 이벤트 처리 부분을 더 선언적으로 만들기
        const relevantFields = ['enable', 'title'];
        const hasRelevantChanges = (old: Todo, new_: Todo) =>
            relevantFields.some(
                field => old[field as keyof Todo] !== new_[field as keyof Todo]
            );

        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'todo',
                filter: `goal_id=eq.${goalId}`,
            },
            payload => {
                if (
                    hasRelevantChanges(payload.old as Todo, payload.new as Todo)
                ) {
                    debouncedInvalidate();
                }
            }
        );

        const subscription = channel.subscribe((status: string) => {
            if (status === 'SUBSCRIBED') {
                console.log(`Subscribed to goal-${goalId}-realtime`);
            } else if (status === 'CHANNEL_ERROR') {
                console.error(`Error subscribing to goal-${goalId}-realtime`);
            }
        });

        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            void subscription.unsubscribe();
        };
    }, [goalId, queryClient]);

    return useSafeQuery({
        queryKey: ['goal', goalId, 'detail'],
        queryFn: async () => {
            // return goalService.getGoalDetailsById(goalId);
            try {
                return await goalService.getGoalDetailsById(goalId);
            } catch (error) {
                console.error('Goal detail query error:', error);
                throw error;
            }
        },
        enabled: !!goalId,
    });
};

// 프로젝트 ID 기반으로 목표들의 상세 정보(리더와 참여자 포함) 조회 훅
export function useGoalDetailsByProjectId(projectId: string) {
    const currentTeam = useTeamStore.getState().currentTeam;

    return useSafeQuery<GoalWithInfos[], Error>({
        queryKey: ['goal', 'team', currentTeam?.id, 'project', projectId],
        queryFn: () => goalService.getGoalDetailsByProjectId(projectId),
        enabled: !!projectId, // projectId가 있을 때만 쿼리 실행
    });
}

// 단일 목표 조회 훅
export function useGoalById(goalId: string, options?: { enabled?: boolean }) {
    const enabled = options?.enabled !== undefined ? options.enabled : true;

    return useSafeQuery<Goal, Error>({
        queryKey: ['goal', goalId],
        queryFn: () => goalService.getGoalById(goalId),
        enabled: enabled && !!goalId, // goalId가 있을 때만 쿼리 실행
    });
}

// 사용자 목표 조회 훅 - 참여자인 목표
export function useUserGoals(
    userId: string | undefined,
    enableCondition?: boolean,
    visibility: GoalVisibilityFilter = GoalVisibilityFilter.VISIBLE
) {
    const isEnabled =
        enableCondition !== undefined ? !!userId && enableCondition : !!userId;

    return useSafeQuery<Partial<GoalWithInfos>[], Error>({
        queryKey: ['goal', 'user', userId],
        queryFn: () => goalService.getUserGoals(userId, visibility),
        enabled: isEnabled,
    });
}

/**
 * 멤버별 목표 조회 훅 - 특정 워크스페이스에서 참여자/관리자인 목표 조회
 * @param userId 멤버의 UID
 * @param workspaceTeamIds 팀 ID 배열
 * @param workspaceId 워크스페이스 ID (제공 시 워크스페이스 컨텍스트로 동작, 없으면 팀 컨텍스트로 동작)
 */
export function useWorkspaceMemberGoals(
    userId: string,
    workspaceTeamIds: string[],
    workspaceId?: string
) {
    const currentTeam = useTeamStore.getState().currentTeam;

    const queryKey = workspaceId
        ? ['goal', 'workspace', workspaceId, 'member', userId]
        : ['goal', 'team', currentTeam?.id, 'member', userId];

    return useSafeQuery<GoalWithProject[], Error>({
        queryKey,
        queryFn: () =>
            goalService.getMemberGoalsByTeamIds(userId, workspaceTeamIds),
        enabled: !!userId && !!workspaceTeamIds.length,
    });
}

// 참여자 업데이트 액션 타입
type ParticipantAction = 'add' | 'remove';

// 통합된 변수 타입
export interface UpdateGoalParticipantVariables {
    action: ParticipantAction;
    goalId: string;
    userId: string;
    registerId?: string; // add 액션에만 필요
}

export function useUpdateGoalParticipant() {
    const queryClient = useQueryClient();
    const currentTeam = useTeamStore.getState().currentTeam;
    const { user } = useAuthStore.getState();

    return useMutation<boolean, Error, UpdateGoalParticipantVariables>({
        mutationFn: async ({ action, goalId, userId, registerId }) => {
            if (action === 'add' && registerId) {
                return goalService.addGoalParticipant(
                    goalId,
                    userId,
                    registerId
                );
            } else if (action === 'remove') {
                return goalService.removeGoalParticipant(goalId, userId);
            }
            throw new Error(
                '유효하지 않은 액션이거나 필요한 매개변수가 누락되었습니다'
            );
        },
        onSuccess: async (_, variables) => {
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'team', currentTeam?.id],
            });

            // 개별 목표 관련 쿼리들 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', variables.goalId],
            });

            // 멤버 목표 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'user', variables.userId],
            });

            // 플래너 목표 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['planners', 'selectedDate', user?.id],
            });
            await queryClient.invalidateQueries({
                queryKey: ['planners', 'unfinished', user?.id],
            });
        },
        onError: error => {
            // 에러 로깅 및 사용자에게 알림
            const actionType = error.message.includes('추가') ? '추가' : '제거';
            console.error(`참여자 ${actionType} 실패:`, error);
        },
    });
}

// 목표 생성 뮤테이션 훅
export function useCreateGoal() {
    const queryClient = useQueryClient();
    const currentTeam = useTeamStore.getState().currentTeam;

    return useMutation<Goal, Error, CreateGoalParams>({
        mutationFn: async newGoal => await goalService.createGoal(newGoal),
        onSuccess: async (_, variables) => {
            // 특정 팀의 목표 목록 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'team', currentTeam?.id],
            });

            // 부모 목표가 있는 경우 해당 목표의 상세 정보도 무효화
            if (variables.parent_id) {
                await queryClient.invalidateQueries({
                    queryKey: ['goal', variables.parent_id],
                });
            }
        },
    });
}

// 목표 수정 뮤테이션 훅
export function useUpdateGoal() {
    const queryClient = useQueryClient();
    const currentTeam = useTeamStore.getState().currentTeam;

    // 알림 발송 함수 참조
    const { createGoalPriorityChangedNotification } =
        useGoalPriorityChangedNotification();
    const { createGoalStateChangedNotification } =
        useGoalStateChangedNotification();
    const { createGoalDeadlineChangedNotification } =
        useGoalDeadlineChangedNotification();
    const { createGoalLeaderChangedNotification } =
        useGoalLeaderChangedNotification();
    const deleteSchedule = useDeleteSchedule();
    const invalidateActivityLogs = useInvalidateActivityLogs();

    return useMutation<
        Goal,
        Error,
        { id: string } & Partial<Goal>,
        UpdateContext<Goal>
    >({
        mutationFn: updatedGoal =>
            goalService.updateGoal(updatedGoal.id, updatedGoal),

        onMutate: async variables => {
            // 이전 데이터 가져오기
            const oldGoalData = await goalService.getGoalById(variables.id);

            // 변경 유형 확인
            const changeTypes = {
                isPriorityChange: isDifferent(
                    variables.priority_code,
                    oldGoalData.priority_code
                ),
                isStatusChange: isDifferent(
                    variables.status_code,
                    oldGoalData.status_code
                ),
                isDeadlineChange:
                    isDifferent(variables.start_date, oldGoalData.start_date) ||
                    isDifferent(variables.end_date, oldGoalData.end_date),
                isLeaderChange: isDifferent(
                    variables.leader_id,
                    oldGoalData.leader_id
                ),
            };

            return {
                oldData: oldGoalData,
                changeTypes,
            };
        },
        onSuccess: async (updatedGoal, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'team', currentTeam?.id],
            });

            // 개별 목표 관련 쿼리들 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', updatedGoal.id],
                exact: false,
            });

            // 부모 목표 관련 쿼리 무효화
            if (updatedGoal.parent_id) {
                await queryClient.invalidateQueries({
                    queryKey: ['goal', updatedGoal.parent_id],
                    exact: false,
                });
            }

            // 목표 로그 캐시 무효화
            invalidateActivityLogs(updatedGoal.id);

            // 변경 관련 알림 발송
            if (!context || !context.oldData) {
                console.log(
                    'Failed to send goal update notification: context or oldData is undefined'
                );
                return;
            }

            // 알림 발송 조건 체크 - Active 상태인 목표거나 상태 변경일 경우에만 알림 발송
            if (
                !checkSendNotificationByStatusCode(
                    updatedGoal,
                    context.changeTypes
                )
            ) {
                return;
            }

            try {
                const { user } = useAuthStore.getState();

                // 완료 상태로 변경 시, 알림 스케줄링 데이터 삭제
                if (
                    context.changeTypes.isStatusChange &&
                    CompleteStatusCodes.includes(
                        updatedGoal.status_code as StatusCode
                    )
                ) {
                    deleteSchedule.mutate({
                        relatedType: 'goal',
                        relatedId: updatedGoal.id,
                        notificationType:
                            NotificationType.GOAL_DEADLINE_REMINDER,
                    });
                }

                // 목표 참여자 정보 가져오기
                const participantIds =
                    await goalService.getGoalParticipantIdsByGoalId(
                        updatedGoal.id
                    );

                // 관리자는 포함, 업데이트 행위자는 제외
                const relatedUserIds = [
                    ...new Set([...participantIds, updatedGoal.leader_id]),
                ];

                // 멤버 목표 쿼리 무효화
                for (const userId of relatedUserIds) {
                    await queryClient.invalidateQueries({
                        queryKey: ['goal', 'user', userId],
                    });
                }

                // 워크스페이스 멤버십 확인을 비동기로 수행
                const membershipChecks = await Promise.all(
                    relatedUserIds.map(async userId => {
                        try {
                            // 워크스페이스 멤버십 권한 확인
                            const membershipData =
                                await workspaceService.checkUserWorkspaceMembership(
                                    updatedGoal.team_id,
                                    userId
                                );
                            return {
                                userId,
                                isAllowed: membershipData.isAllowed,
                            };
                        } catch (error) {
                            console.error(
                                `워크스페이스 멤버십 확인 실패 (유저 ID: ${userId}):`,
                                error
                            );
                            return { userId, isAllowed: false };
                        }
                    })
                );

                // 권한이 있는 사용자만 필터링
                const uniqueParticipantIds = membershipChecks
                    .filter(check => check.isAllowed)
                    .map(check => check.userId);

                const sendNotiUserIds = uniqueParticipantIds.filter(
                    userId => userId !== user?.id
                );

                const { changeTypes } = context;
                const notificationPromises = [];
                const schedulePromises = [];

                // 공통 데이터
                const commonData = {
                    // goalId: updatedGoal.id,
                    goal_title: updatedGoal.title,
                    changer_name: user.name,
                    changer_image_url: user.profile_image_url || '',
                    changer_id: user.id,
                    team_id: updatedGoal.team_id,
                };

                // 1. 우선순위 변경 알림
                if (changeTypes.isPriorityChange && variables.priority_code) {
                    notificationPromises.push(
                        ...sendNotiUserIds.map(userId =>
                            createGoalPriorityChangedNotification({
                                userId,
                                goalId: updatedGoal.id,
                                goalPriorityChangedContent: {
                                    ...commonData,
                                    old_priority: context.oldData.priority_code,
                                    new_priority: variables.priority_code,
                                },
                            })
                        )
                    );
                }

                // 2. 상태 변경 알림
                if (changeTypes.isStatusChange && variables.status_code) {
                    notificationPromises.push(
                        ...sendNotiUserIds.map(userId =>
                            createGoalStateChangedNotification({
                                userId,
                                goalId: updatedGoal.id,
                                goalStateChangedContent: {
                                    ...commonData,
                                    old_state: context.oldData.status_code,
                                    new_state: variables.status_code,
                                },
                            })
                        )
                    );
                }

                // 3. 마감기한 변경 알림
                if (
                    changeTypes.isDeadlineChange &&
                    (variables.start_date || variables.end_date)
                ) {
                    notificationPromises.push(
                        ...sendNotiUserIds.map(userId =>
                            createGoalDeadlineChangedNotification({
                                userId,
                                goalId: updatedGoal.id,
                                goalDeadlineChangedContent: {
                                    ...commonData,
                                    old_start_date: context.oldData.start_date,
                                    old_end_date: context.oldData.end_date,
                                    new_start_date: variables.start_date,
                                    new_end_date: variables.end_date,
                                },
                            })
                        )
                    );

                    // 알림 스케줄링 삭제 - 새 스케줄 추가는 매시간 실행되는 rpc 함수로 처리
                    deleteSchedule.mutate({
                        relatedType: 'goal',
                        relatedId: updatedGoal.id,
                        notificationType:
                            NotificationType.GOAL_DEADLINE_REMINDER,
                    });
                }

                // 4. 관리자 변경 알림 - 해당 관리자에게만 전송
                if (changeTypes.isLeaderChange && variables.leader_id) {
                    const oldLeader = await authService.getUserById(
                        context.oldData.leader_id
                    );
                    const newLeader = await authService.getUserById(
                        variables.leader_id
                    );

                    if (
                        oldLeader &&
                        newLeader &&
                        oldLeader.id !== newLeader.id
                    ) {
                        notificationPromises.push(
                            createGoalLeaderChangedNotification({
                                userId: newLeader.id,
                                goalId: updatedGoal.id,
                                goalLeaderChangedContent: {
                                    ...commonData,
                                    old_leader_name: oldLeader.name,
                                    old_leader_id: oldLeader.id,
                                    new_leader_name: newLeader.name,
                                    new_leader_id: newLeader.id,
                                },
                            })
                        );
                    }
                }

                // 모든 알림 한 번에 처리
                await Promise.all(notificationPromises);
                await Promise.all(schedulePromises);
            } catch (error) {
                console.error('알림 발송 또는 스케줄 등록 실패:', error);
                // 주요 업데이트는 이미 성공했으므로 에러를 무시
            }
        },
    });
}

// 목표 숨기기 관련 훅
export function useHideGoal() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            goalId,
            userId,
        }: {
            goalId: string;
            userId: string;
        }) => {
            return goalService.hideGoal(goalId, userId);
        },
        onSuccess: async (_, variables) => {
            // 관련된 모든 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'user', variables.userId],
            });
            await queryClient.invalidateQueries({
                queryKey: ['goal', variables.goalId],
            });

            await queryClient.invalidateQueries({ queryKey: ['todos'] });
            await queryClient.invalidateQueries({ queryKey: ['planners'] });
        },
    });
}

export function useRevealGoal() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            goalId,
            userId,
        }: {
            goalId: string;
            userId: string;
        }) => {
            return goalService.revealGoal(goalId, userId);
        },
        onSuccess: async (_, variables) => {
            // 관련된 모든 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'user', variables.userId],
            });
            await queryClient.invalidateQueries({
                queryKey: ['goal', variables.goalId],
            });

            await queryClient.invalidateQueries({ queryKey: ['todos'] });
            await queryClient.invalidateQueries({ queryKey: ['planners'] });
        },
    });
}

// 목표 삭제 뮤테이션 훅
export function useDeleteGoal() {
    const queryClient = useQueryClient();
    const currentTeam = useTeamStore.getState().currentTeam;

    const deleteNotificationSchedule = useDeleteSchedule();
    const invalidateActivityLogs = useInvalidateActivityLogs();

    return useMutation<void, Error, string>({
        mutationFn: goalId => goalService.deleteGoal(goalId),
        onSuccess: async (_, goalId) => {
            // 목표 목록 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'team', currentTeam?.id],
            });

            // 개별 목표 관련 쿼리들 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', goalId],
            });

            // 활동 로그 캐시 무효화
            invalidateActivityLogs(goalId);

            // 마감기한 알림 스케줄 삭제
            try {
                await deleteNotificationSchedule.mutateAsync({
                    relatedType: 'goal',
                    relatedId: goalId,
                    notificationType: NotificationType.GOAL_DEADLINE_REMINDER,
                });
            } catch (error) {
                console.error('Failed to delete notification schedule:', error);
            }
        },
        onError: error => {
            console.error('Failed to delete goal:', error);
        },
    });
}

// 목표 댓글 관련 쿼리
export function useGoalComments(goalId: string | undefined) {
    return useComments('goal', goalId);
}

// 목표 공유 상태 업데이트 뮤테이션 훅 (공유 또는 공유 취소)
export function useUpdateGoalSharing() {
    const queryClient = useQueryClient();
    const currentTeam = useTeamStore.getState().currentTeam;

    const invalidateActivityLogs = useInvalidateActivityLogs();

    return useMutation({
        mutationFn: ({
            goalId,
            teamId,
            enable,
            includeChildGoals = false,
        }: {
            goalId: string;
            teamId: string;
            enable: boolean;
            includeChildGoals?: boolean;
        }) =>
            goalService.updateGoalSharing(
                goalId,
                teamId,
                enable,
                includeChildGoals
            ),
        onSuccess: (_, variables) => {
            // 목표 공유 상태 변경 후 관련 데이터 갱신
            void queryClient.invalidateQueries({
                queryKey: ['goal', 'team', currentTeam?.id],
            });
            void queryClient.invalidateQueries({
                queryKey: ['goal', 'team', variables.teamId],
            });
            void queryClient.invalidateQueries({
                queryKey: ['goal', variables.goalId],
            });

            // 활동 로그 캐시 무효화
            invalidateActivityLogs(variables.goalId);
        },
    });
}

// 하위 목표들의 공유 상태 확인 뮤테이션 훅
export function useCheckChildrenSharingStatus() {
    return useMutation({
        mutationFn: ({ goalId, teamId }: { goalId: string; teamId: string }) =>
            goalService.checkChildrenSharingStatus(goalId, teamId),
    });
}

// 상위 목표들을 재귀적으로 가져오는 훅
export function useParentGoals(goalId: string | undefined) {
    return useQuery<Goal[], Error>({
        queryKey: ['goal', goalId, 'parent-goals'],
        queryFn: async () => {
            if (!goalId) return [];

            try {
                const parents: Goal[] = [];
                const currentGoal = await goalService.getGoalById(goalId);
                let currentParentId = currentGoal?.parent_id;

                while (currentParentId) {
                    const parent =
                        await goalService.getGoalById(currentParentId);
                    if (parent) {
                        // 상위 목표임을 표시하는 플래그 추가
                        const parentWithMeta = {
                            ...parent,
                            isParent: true,
                        };
                        parents.push(parentWithMeta);
                        currentParentId = parent.parent_id;
                    } else {
                        currentParentId = null;
                    }
                }

                return parents;
            } catch (error) {
                console.error('상위 목표 조회 오류:', error);
                throw error;
            }
        },
        enabled: !!goalId,
    });
}

// 목표 접근 권한 확인 훅
export function useCheckGoalAccess(goalId?: string) {
    return useQuery({
        queryKey: ['goalAccess', goalId],
        queryFn: () => goalService.checkGoalAccess(goalId),
        enabled: !!goalId,
        staleTime: 5 * 60 * 1000, // 5분
    });
}
